import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calculator, ClipboardText, FileArrowUp, Percent, Truck } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import CompanyParamsPanel from '../components/pricing/CompanyParamsPanel';
import NfeImports from '../components/pricing/NfeImports';
import RulesPanel from '../components/pricing/RulesPanel';
import SkuCostList from '../components/pricing/SkuCostList';
import SupplierProductsPanel from '../components/pricing/SupplierProductsPanel';

/**
 * PRECIFICAÇÃO — custo fiscal por SKU (modelo v5 da Greycom). Cinco painéis
 * irmãos, mesmo visual de abas da Inteligência de Mercado: o cadastro de
 * custos por SKU (porta de entrada), as NF-e de compra importadas, o de-para
 * fornecedor → produto, as regras de tratamento de ICMS e os parâmetros da
 * empresa. A aba vive na
 * URL (?tab=) para os cards e a página do SKU poderem apontar para ela.
 */
const TABS = [
    { id: 'skus', label: 'Custos por SKU', icon: Calculator },
    { id: 'nfe', label: 'Notas de entrada', icon: FileArrowUp },
    { id: 'suppliers', label: 'De-para de fornecedores', icon: Truck },
    { id: 'rules', label: 'Regras de ICMS', icon: ClipboardText },
    { id: 'params', label: 'Parâmetros fiscais', icon: Percent },
];
const TAB_IDS = new Set(TABS.map((tab) => tab.id));

const Pricing = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const requestedTab = new URLSearchParams(location.search).get('tab');
    const [activeTab, setActiveTab] = useState(TAB_IDS.has(requestedTab) ? requestedTab : 'skus');

    useEffect(() => {
        if (TAB_IDS.has(requestedTab)) {
            setActiveTab(requestedTab);
        }
    }, [requestedTab]);

    const selectTab = (id) => {
        setActiveTab(id);
        const params = new URLSearchParams(location.search);
        params.set('tab', id);
        navigate({ search: `?${params.toString()}` }, { replace: true });
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Precificação"
                subtitle="Custo líquido de entrada por tratamento de ICMS, regras por SKU/NCM e NF-e de compra"
            />

            <div className="border-b border-mono-200 dark:border-mono-800">
                <nav className="flex space-x-8 overflow-x-auto">
                    {TABS.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = tab.id === activeTab;
                        return (
                            <button
                                type="button"
                                key={tab.id}
                                onClick={() => selectTab(tab.id)}
                                aria-current={isActive ? 'true' : undefined}
                                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex items-center gap-2 ${
                                    isActive
                                        ? 'border-black dark:border-white text-black dark:text-white'
                                        : 'border-transparent text-mono-500 hover:text-mono-700 dark:hover:text-mono-300 hover:border-mono-300'
                                }`}
                            >
                                <Icon size={18} />
                                {tab.label}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Render condicional: montar ao entrar na aba garante o refetch. */}
            {activeTab === 'skus' && <SkuCostList onGoToTab={selectTab} />}
            {activeTab === 'nfe' && <NfeImports />}
            {activeTab === 'suppliers' && <SupplierProductsPanel />}
            {activeTab === 'rules' && <RulesPanel />}
            {activeTab === 'params' && <CompanyParamsPanel />}
        </div>
    );
};

export default Pricing;
